/* eslint-disable max-len */
import { useEffect, useState, useRef } from 'react';
import {
  Box, Button, Typography, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, ToggleButton, ToggleButtonGroup, Divider, InputAdornment,
  IconButton, Menu, MenuItem, CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import RefreshIcon from '@mui/icons-material/Refresh';
import WifiIcon from '@mui/icons-material/Wifi';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import SearchIcon from '@mui/icons-material/Search';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useRemoteScore } from '../hooks/useRemoteScore';
import { buildQueueSections, formatWaitingFor } from '../utils/queue';
import { NextTeamDialog } from '../components/NextTeamDialog';

const CARD_BG = 'rgba(255, 255, 255, 0.7)';

export default function RemoteControl() {
  // Trocar manifest para que "Add to Home Screen" use /remote como start_url
  useEffect(() => {
    const manifestLink = document.querySelector('link[rel="manifest"]');
    if (manifestLink) manifestLink.href = '/manifest-remote.json';
    return () => {
      if (manifestLink) manifestLink.href = '/manifest.json';
    };
  }, []);

  const {
    isConnected, error, connect, disconnect, remoteState, sendCommand,
  } = useRemoteScore();

  // Placar vem do app (fonte da verdade), via snapshot.
  const scoreA = remoteState?.game?.scoreA ?? 0;
  const scoreB = remoteState?.game?.scoreB ?? 0;

  const [pendingAction, setPendingAction] = useState(null);
  const pendingTimeoutRef = useRef(null);

  // Quando o placar do app atualiza, libera o loader do botão.
  useEffect(() => {
    setPendingAction(null);
    if (pendingTimeoutRef.current) clearTimeout(pendingTimeoutRef.current);
  }, [scoreA, scoreB]);

  const handleScore = (team, action) => {
    setPendingAction(`${team}-${action}`);
    if (pendingTimeoutRef.current) clearTimeout(pendingTimeoutRef.current);
    // Segurança: se não vier confirmação em 3s, libera o botão.
    pendingTimeoutRef.current = setTimeout(() => setPendingAction(null), 3000);
    sendCommand({ type: action === 'add' ? 'addScore' : 'removeScore', payload: team });
  };

  const [newPlayerOpen, setNewPlayerOpen] = useState(false);
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newGender, setNewGender] = useState('M');
  const [newPoints, setNewPoints] = useState('50');
  const [search, setSearch] = useState('');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuPlayer, setMenuPlayer] = useState(null);
  const [nextTeamOpen, setNextTeamOpen] = useState(false);

  const players = remoteState?.players || [];
  const playersPerTeam = remoteState?.game?.playersPerTeam || 6;
  const sections = buildQueueSections(players);
  const totalCheckedIn = players.filter((p) => p.checkedIn).length;
  const teamACount = players.filter((p) => p.teamId === 1).length;
  const teamBCount = players.filter((p) => p.teamId === 2).length;
  const gameStarted = remoteState?.game?.gameStarted;
  const teamsFull = teamACount === playersPerTeam && teamBCount === playersPerTeam;
  const scoreTied = scoreA === scoreB;

  const handleResetScore = () => {
    if (window.confirm('Zerar o placar da partida?')) sendCommand({ type: 'resetScore' });
  };
  const handleEndGame = () => {
    const winner = scoreA > scoreB ? 'A' : 'B';
    if (window.confirm(`Encerrar a partida ${scoreA} x ${scoreB}? O Time ${winner} será declarado vencedor.`)) {
      sendCommand({ type: 'endGame' });
    }
  };

  const suggestion = remoteState?.nextTeamSuggestion || { targetTeamId: 1, playerIds: [] };

  const addToNextTeam = (playerId) => {
    const isAFull = teamACount >= playersPerTeam;
    const isBFull = teamBCount >= playersPerTeam;
    if (isAFull && isBFull) return;
    const teamId = isAFull ? 2 : 1;
    sendCommand({ type: 'setPlayerTeam', payload: { playerId, teamId } });
  };

  const openMenu = (e, player) => {
    setMenuAnchor(e.currentTarget);
    setMenuPlayer(player);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuPlayer(null);
  };
  const runOnMenuPlayer = (type) => {
    if (menuPlayer) sendCommand({ type, payload: menuPlayer.playerId });
    closeMenu();
  };
  const menuHasPriority = (menuPlayer?.waitingFor ?? 0) >= 10;

  const handleCreatePlayer = () => {
    if (!newName.trim()) return;
    sendCommand({
      type: 'createNewPlayer',
      payload: { playerName: newName.trim(), gender: newGender, totalPoints: Number(newPoints) },
    });
    setNewName('');
    setNewGender('M');
    setNewPoints('50');
    setNewPlayerOpen(false);
  };

  const notCheckedIn = players
    .filter((p) => !p.checkedIn)
    .filter((p) => p.playerName.toLowerCase().includes(search.toLowerCase()));

  return (
    <Box sx={{
      minHeight: '100vh', p: 2, bgcolor: '#ffbcbc',
    }}
    >
      <Typography variant="h5" color="black" textAlign="center" mb={2}>
        Controle Remoto
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        {isConnected ? (
          <Button variant="contained" color="error" startIcon={<WifiOffIcon />} onClick={disconnect}>
            Desconectar
          </Button>
        ) : (
          <Button variant="contained" color="success" startIcon={<WifiIcon />} onClick={connect}>
            Conectar
          </Button>
        )}
      </Box>

      {error && <Typography color="error" textAlign="center" mb={2}>{error}</Typography>}

      {/* Placar */}
      <Box sx={{
        display: 'flex', gap: 4, justifyContent: 'center', mb: 3,
      }}
      >
        {['A', 'B'].map((team) => (
          <Box
            key={team}
            sx={{
              display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center',
            }}
          >
            <Typography variant="h6" color="black">{`Time ${team}`}</Typography>
            <Typography variant="h3" color="black" sx={{ fontWeight: 700, lineHeight: 1 }}>
              {team === 'A' ? scoreA : scoreB}
            </Typography>
            <Button variant="contained" color={team === 'A' ? 'primary' : 'secondary'} onClick={() => handleScore(team, 'add')} disabled={!isConnected || pendingAction === `${team}-add`} sx={{ width: 100, height: 56 }}>
              {pendingAction === `${team}-add` ? <CircularProgress size={26} color="inherit" /> : <AddIcon fontSize="large" />}
            </Button>
            <Button variant="outlined" color={team === 'A' ? 'primary' : 'secondary'} onClick={() => handleScore(team, 'remove')} disabled={!isConnected || pendingAction === `${team}-remove`} sx={{ width: 100, height: 36, bgcolor: '#fff' }}>
              {pendingAction === `${team}-remove` ? <CircularProgress size={18} color="inherit" /> : <RemoveIcon />}
            </Button>
          </Box>
        ))}
      </Box>

      <Box sx={{
        display: 'flex', gap: 1, justifyContent: 'center', mb: 2,
      }}
      >
        <Button variant="outlined" color="warning" startIcon={<RefreshIcon />} onClick={handleResetScore} disabled={!isConnected} sx={{ bgcolor: '#fff' }}>
          Resetar Placar
        </Button>
        {gameStarted ? (
          <Button variant="contained" color="error" onClick={handleEndGame} disabled={!isConnected || scoreTied}>
            Finalizar partida
          </Button>
        ) : (
          <Button variant="contained" color="success" onClick={() => sendCommand({ type: 'startGame' })} disabled={!isConnected || !teamsFull}>
            Iniciar partida
          </Button>
        )}
      </Box>

      {/* Ações: novo jogador / check-in */}
      <Box sx={{
        display: 'flex', gap: 1, justifyContent: 'center', mb: 3,
      }}
      >
        <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => setNewPlayerOpen(true)} disabled={!isConnected}>
          Novo jogador
        </Button>
        <Button variant="contained" color="secondary" startIcon={<HowToRegIcon />} onClick={() => setCheckInOpen(true)} disabled={!isConnected}>
          Check-in
        </Button>
      </Box>

      {/* Montar próximo time */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        <Button variant="contained" color="primary" onClick={() => setNextTeamOpen(true)} disabled={!isConnected}>
          Montar próximo time
        </Button>
      </Box>

      {/* Fila em seções */}
      <Typography variant="h6" color="black" textAlign="center" mb={1}>
        {`${totalCheckedIn} jogadores`}
      </Typography>
      <Box sx={{
        maxWidth: 520, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 2,
      }}
      >
        {!isConnected && (
          <Typography textAlign="center" color="text.secondary">Conecte para ver a fila.</Typography>
        )}
        {isConnected && sections.length === 0 && (
          <Typography textAlign="center" color="text.secondary">Nenhum jogador na fila.</Typography>
        )}
        {sections.map((section) => (
          <Box key={section.key} sx={{ bgcolor: CARD_BG, borderRadius: 3, p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, textAlign: 'center', mb: 1 }}>
              {section.title}
            </Typography>
            {section.players.map((p) => (
              <Box
                key={p.playerId}
                sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5,
                }}
              >
                <Box>
                  <Typography variant="body1">
                    {p.playerName}
                    {p.gender === 'F' ? ' ♀' : ''}
                    {p.stayIn ? ' 📌' : ''}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatWaitingFor(p.waitingFor, p.checkedInNow)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Button size="small" variant="contained" onClick={() => addToNextTeam(p.playerId)}>
                    Jogar
                  </Button>
                  <IconButton size="small" onClick={(e) => openMenu(e, p)}>
                    <MoreVertIcon />
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Box>
        ))}
      </Box>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        <MenuItem onClick={() => runOnMenuPlayer(menuHasPriority ? 'removePriority' : 'addPriority')}>
          {menuHasPriority ? 'Remover próxima' : 'Próxima'}
        </MenuItem>
        <MenuItem onClick={() => runOnMenuPlayer('toggleStayIn')}>
          {menuPlayer?.stayIn ? 'Regular' : 'Stay In'}
        </MenuItem>
        <MenuItem onClick={() => runOnMenuPlayer('checkOut')}>Checkout</MenuItem>
      </Menu>

      {/* Modal: novo jogador */}
      <Dialog open={newPlayerOpen} onClose={() => setNewPlayerOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Novo jogador</DialogTitle>
        <DialogContent sx={{
          display: 'flex', flexDirection: 'column', gap: 2, pt: 1,
        }}
        >
          <TextField label="Nome" value={newName} onChange={(e) => setNewName(e.target.value)} autoFocus fullWidth sx={{ mt: 1 }} />
          <ToggleButtonGroup value={newGender} exclusive onChange={(e, v) => v && setNewGender(v)} size="small">
            <ToggleButton value="M">M</ToggleButton>
            <ToggleButton value="F">F</ToggleButton>
          </ToggleButtonGroup>
          <TextField label="Pontos (0 a 100)" type="number" value={newPoints} onChange={(e) => setNewPoints(e.target.value)} inputProps={{ min: 0, max: 100 }} fullWidth />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewPlayerOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreatePlayer}>Adicionar</Button>
        </DialogActions>
      </Dialog>

      {/* Modal: check-in de jogador existente */}
      <Dialog open={checkInOpen} onClose={() => setCheckInOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Check-in de jogador</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <TextField
            placeholder="Buscar jogador..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            fullWidth
            sx={{ mt: 1, mb: 2 }}
            InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }}
          />
          {notCheckedIn.length === 0 && (
            <Typography color="text.secondary" textAlign="center">Nenhum jogador encontrado.</Typography>
          )}
          {notCheckedIn.map((p) => (
            <Box key={p.playerId}>
              <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1,
              }}
              >
                <Typography>
                  {p.playerName}
                  {p.gender === 'F' ? ' ♀' : ''}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" variant="contained" onClick={() => sendCommand({ type: 'checkIn', payload: p.playerId })}>Agora</Button>
                  <Button size="small" variant="outlined" onClick={() => sendCommand({ type: 'checkInWithPriority', payload: p.playerId })}>Próxima</Button>
                </Box>
              </Box>
              <Divider />
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCheckInOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <NextTeamDialog
        open={nextTeamOpen}
        onClose={() => setNextTeamOpen(false)}
        players={players}
        playersPerTeam={playersPerTeam}
        suggestion={suggestion}
        config={remoteState?.game || {}}
        onFillTeam={(ids, priorityIds) => sendCommand({ type: 'fillTeam', payload: { playerIds: ids, priorityIds } })}
        onApplyAdjustment={(adj) => sendCommand({ type: 'applyAdjustment', payload: { setTeamOps: adj.setTeamOps, priorityIds: adj.priorityIds } })}
      />
    </Box>
  );
}
